import { toast } from 'sonner';
import { useExportCampaignPagesStaticUrls } from '@tinct/core-api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@tinct/web-components';
import { CloudDownload } from 'lucide-react';

export type ExportUrlsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
};

export function ExportUrlsModal({
  open,
  onOpenChange,
  campaignId,
  campaignName,
}: ExportUrlsModalProps) {
  const { mutateAsync: exportUrls, isPending } = useExportCampaignPagesStaticUrls();

  const handleExport = () => {
    const downloadPromise = exportUrls({ id: campaignId }).then((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${campaignName || `campaign-${campaignId}`}-static-urls.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    });

    toast.promise(downloadPromise, {
      loading: 'Generating file...',
      success: 'File generated and downloaded',
      error: 'Failed to generate file',
    });

    downloadPromise.finally(() => onOpenChange(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="max-w-md gap-0 overflow-hidden rounded-3xl p-0"
      >
        {/* Content */}
        <div className="flex flex-col gap-3 p-8">
          <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground font-heading text-center">
            Export unique URLs
          </DialogTitle>
          <p className="text-base text-muted-foreground">
            Each link is tied to one account and personalizes for whoever opens it: no IP needed. Ideal for cold email, DMs, and outbound.
          </p>
          <p className="text-base text-muted-foreground">
            For LinkedIn Ads, only use these if each account has its own ad. For one campaign across multiple accounts, keep IP matching.
          </p>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row gap-2 px-8 pb-8">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="flex-1"
            onClick={handleExport}
            disabled={isPending}
          >
            <CloudDownload className="size-4" />
            Export (CSV)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
